import type { Language } from '../store/languageStore';
import type { TranslationKey, Translations } from './types';
import he from './locales/he';

const VALID_LANGS: Language[] = ['he', 'en', 'ru', 'si', 'uz', 'ro'];

const cache: Partial<Record<Language, Translations>> = {
  he,
};

/** Exclude `he` — it is eager-imported above so it is not split into a lazy chunk. */
const localeModules = import.meta.glob<{ default: Translations }>([
  './locales/*.ts',
  '!./locales/he.ts',
]);

export async function loadLocaleIfNeeded(lang: Language): Promise<void> {
  if (cache[lang]) return;
  const path = `./locales/${lang}.ts`;
  const loader = localeModules[path];
  if (!loader) {
    cache[lang] = he;
    return;
  }
  const mod = await loader();
  cache[lang] = mod.default;
}

function readInitialLanguage(): Language {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = localStorage.getItem('hours-language-storage');
      if (raw) {
        const lang = JSON.parse(raw) as Language;
        if (VALID_LANGS.includes(lang)) return lang;
      }
    }
  } catch {
    /* ignore */
  }
  return 'he';
}

/** Call before React mounts so translate() has the user’s locale (reduces initial JS parse on iOS). */
export async function preloadInitialLocale(): Promise<void> {
  const lang = readInitialLanguage();
  await loadLocaleIfNeeded(lang);
}

export function translate(
  key: TranslationKey,
  language: Language,
  params?: Record<string, string | number>,
): string {
  const dict = cache[language] ?? he;
  const translation = dict[key] || he[key] || key;
  if (!params) return translation;
  return translation.replace(/\{(\w+)\}/g, (match, paramKey) => {
    return params[paramKey]?.toString() || match;
  });
}
